import React from 'react';
import { FlagIcon } from '@heroicons/react/24/outline';
import { Field } from './ProResultCard';
import { EdiResultRow } from '../services/pendingEdiOrders.service';
import { renderEdiCardValue, canGetTemplate, canAttach, canCreate, EDI_CARD_MONO_IDS, EdiCardHandlers } from '../utils/ediOrderColumns';
import { urgencyRowClass } from '../utils/ediUrgency';
import EdiRowActions from './EdiRowActions';

// =============================================================================
// EdiResultCard
// =============================================================================
// One pending EDI order rendered as a card for TanTable's card ("Multi Row") mode. The
// field grid is COLUMN-DRIVEN: `fields` is the table's ordered + visible columns (from
// TanTable's renderCardRow ctx), so column show/hide/reorder/filter drive the card exactly
// like the table. Each field's value comes from renderEdiCardValue (the card counterpart of
// the table cells — EDI Info / dupes links, the Size-Type / SCAC / port / location map cells,
// the template edit link, else type-formatted/raw). Reuses the order cards' Field atom for
// the identical yellow click-to-highlight (state lives in the host's <FieldHighlightProvider>).
// Per-record actions ride in a fixed right strip (the __actions column is a cardHeaderColumnId,
// excluded from the field grid).

const EdiResultCard: React.FC<{
  row: EdiResultRow;
  /** Ordered, visible card columns (TanTable renderCardRow ctx.visibleColumns). */
  fields: { id: string; label: string }[];
  /** Field id -> registry type, for formatting non-special fields (date / number). */
  types?: Map<string, string>;
  onEdiInfoClick?: (recno: number) => void;
  onMapLocation?: (recno: number) => void;
  onMapEquipment?: (recno: number) => void;
  onMapPort?: (unlocode: string) => void;
  onMapScac?: (recno: number) => void;
  onAccept?: (recno: number) => void;
  onReject?: (recno: number) => void;
  onDelete?: (recno: number) => void;
  onClear?: (recno: number) => void;
  onGetTemplate?: (recno: number) => void;
  onAttach?: (recno: number) => void;
  onCreate?: (recno: number) => void;
  onEditTemplate?: (recno: number) => void;
}> = ({
  row: r, fields, types,
  onEdiInfoClick, onMapLocation, onMapEquipment, onMapPort, onMapScac,
  onAccept, onReject, onDelete, onClear, onGetTemplate, onAttach, onCreate, onEditTemplate,
}) => {
  const h: EdiCardHandlers = { onEdiInfoClick, onMapLocation, onMapEquipment, onMapPort, onMapScac, onEditTemplate };
  const hasActions = !!(onAccept || onReject || onDelete || onClear || onGetTemplate || onAttach || onCreate);
  // Accept/reject deadline tint (LC-dashboard colours) replaces the default card bg when urgent.
  const urgentBg = urgencyRowClass(r.urgency) || 'bg-gray-50 dark:bg-gray-900/40';

  return (
    <div className={`m-2 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm ${urgentBg}`}>
      {/* Multi-delivery flag (V1's blue highlight, as a chip so it doesn't fight the urgency
          tint). `multistop` = the board's ">1 delivery in contactinformation" computed flag. */}
      {Number(r.multistop) > 0 && (
        <div className="px-3 pt-2 -mb-1">
          <span
            title="Multiple delivery locations — review before creating/attaching"
            className="inline-flex items-center gap-1 rounded-full border border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/30 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300"
          >
            <FlagIcon className="w-3 h-3" /> Multi-Delivery
          </span>
        </div>
      )}
      <div className="flex items-stretch gap-3 px-3 py-2">
        <div className="flex-1 min-w-0 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-8 gap-x-3 gap-y-1">
          {fields.map((f) => (
            <Field
              key={f.id}
              label={f.label}
              value={renderEdiCardValue(f.id, r, h, types?.get(f.id))}
              mono={EDI_CARD_MONO_IDS.has(f.id)}
            />
          ))}
        </div>
        {hasActions && (
          <div className="flex flex-col justify-center pl-3 border-l border-gray-200 dark:border-gray-700 shrink-0">
            <EdiRowActions
              acceptreject={r.acceptreject}
              onAccept={onAccept ? () => onAccept(r.recno) : undefined}
              onReject={onReject ? () => onReject(r.recno) : undefined}
              onDelete={onDelete ? () => onDelete(r.recno) : undefined}
              onClear={onClear ? () => onClear(r.recno) : undefined}
              onGetTemplate={onGetTemplate ? () => onGetTemplate(r.recno) : undefined}
              canGetTemplate={canGetTemplate(r)}
              onAttach={onAttach ? () => onAttach(r.recno) : undefined}
              canAttach={canAttach(r)}
              onCreate={onCreate ? () => onCreate(r.recno) : undefined}
              canCreate={canCreate(r)}
              vertical
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EdiResultCard;
